import {
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { HelpPopover } from '@/design';
import { idTypeLabel } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import {
  brokerCheckSummary,
  type IAdminParticipantDetail,
  type IAdminParticipantFact,
  type IAdminParticipantIdentifier,
  type IAdminParticipantSecAdvFirm
} from '../../api/participant-contracts';
import { absent, instantOrAbsent } from '../../components/account-facts';
import { Fact, RegisterStatusLink, Section } from '../../components/admin-page';
import { FirmLink } from '../../components/firm-link';
import { PayloadTree } from '../../components/payload-tree';
import { RegistryFact } from '../../components/registry-fact';
import {
  FIRM_TYPE,
  LabelledCode,
  codeValues,
  matchedByText,
  useSecAdvCatalogue
} from '../../components/sec-adv-values';
import { TableFrame } from '../../components/table-frame';
import {
  REFRESH_OF_ID_TYPE,
  SEC_ADV_MATCHED_ID_TYPES,
  groupByRegistry,
  type FactReading
} from '../../registries';
import { BrokerCheckReading } from './brokercheck-summary';
import { ReadingHelp, ReadingNote, readingTone, readingWords } from './fact-readings';
import { MemberPart } from './household';
import { NumberRefresh, type INumberWrites } from './numbers';

/**
 * Registry answers (R9): per member, what each registry last said, **as survey-api read it** —
 * one panel per registry a number here is checked against and per source that answered, the SEC
 * ADV register's firms after them. They sat under the numbers on the participant card; a
 * number's own row now says only its last answer and its age, and this is where the answer is
 * read.
 *
 * **A CRD is under two registries** since SR-396: FINRA BrokerCheck's group and SEC IAPD's, each
 * with what its own registry answered — or, under IAPD for a CRD whose answers predate it, "No
 * answer from SEC IAPD is held" — since one lookup asks both (`groupByRegistry`, over
 * `REGISTRY_OF_ID_TYPE`'s pair).
 *
 * **What is drawn is the server's.** A BrokerCheck or IAPD person answer draws its `summary`
 * (`brokercheck-summary.tsx`, naming the registry it came from); `prior_cycle.summary` is a table
 * of its members as stored — the migration's keys, whatever they are, since a key list here would
 * be a copy of the ETL; a summary under a key this app has not typed is drawn whole; every other
 * answer is its sentence alone. **Under every one, the payload as stored**, whole and folded
 * (`RegistryFact`) — the evidence the reading was made from, kept (roadmap D8's storage half).
 *
 * **What is not about the participant's own number is read apart** (SR-416, `READING_OF_FACT_KEY`):
 * Veritant's readings (the wirehouse list, the U4), a team's members looked up and the CRD typed
 * into the FSP survey are three groups after the registries', each in words (`fact-readings.tsx`),
 * with no "Ask … again" — no press here refreshes them directly.
 *
 * **"Ask … again" is the number's**, offered again under each of its registries' answers
 * (`NumberRefresh`) — a CRD's "Ask BrokerCheck and IAPD again" under both groups, since its one
 * press asks both: the same press as the one in the number's row, held while anything waits on
 * the number, and what it was told said in every place — the notes are the page's, keyed by the
 * number, so there is one note however many places it is read.
 */
export function RegistryAnswersSection({
  members,
  anchor,
  writes,
  secAdvElsewhere = false
}: {
  members: IAdminParticipantDetail[];
  anchor: string;
  writes: INumberWrites;
  /** A firm's or organisation's record page, whose SEC ADV section draws the register's entry
   *  (SR-407): the group here would be the same firms a second time. */
  secAdvElsewhere?: boolean;
}) {
  const multi = members.length > 1;
  // Decided off the hub document, before any read: no member holds an answer or a register firm.
  const empty = !members.some((member) => holdsAnswers(member, secAdvElsewhere));
  return (
    <Section title="Registry answers" anchor={anchor} empty={empty}>
      <CardContent className="grid gap-8">
        {members.map((member, index) => (
          <MemberPart key={member.participantId} member={member} opened={index === 0} multi={multi}>
            <MemberAnswers
              member={member}
              multi={multi}
              writes={writes}
              secAdvElsewhere={secAdvElsewhere}
            />
          </MemberPart>
        ))}
      </CardContent>
    </Section>
  );
}

/** Whether a member's part holds anything but "no answer" sentences: an answer from any source,
 *  or the SEC ADV register's group — so the section can say it is empty before it draws. */
function holdsAnswers(member: IAdminParticipantDetail, secAdvElsewhere: boolean): boolean {
  return (
    member.facts.length > 0 ||
    (!secAdvElsewhere && Boolean(secAdvShown(member.secAdvFirms, member.identifiers)))
  );
}

function MemberAnswers({
  member,
  multi,
  writes,
  secAdvElsewhere
}: {
  member: IAdminParticipantDetail;
  multi: boolean;
  writes: INumberWrites;
  secAdvElsewhere: boolean;
}) {
  const Heading = multi ? 'h4' : 'h3';
  const { identifiers, facts } = member;
  // Every registry a number here is checked against, and every source that answered — "Other
  // identifiers" holds no answer.
  const groups = groupByRegistry(identifiers, facts).filter(
    (group) => group.registry !== null || group.facts.length > 0
  );
  const secAdv = !secAdvElsewhere && secAdvShown(member.secAdvFirms, identifiers);
  if (groups.length === 0 && !secAdv) {
    return <p className="text-sm">{absent('No registry answer is held for this participant.')}</p>;
  }

  return (
    <div className="grid gap-6">
      {groups.map((group) => {
        const refreshable = group.numbers.filter((number) => REFRESH_OF_ID_TYPE.has(number.idType));
        const [first] = refreshable;
        const retry = first ? (REFRESH_OF_ID_TYPE.get(first.idType) ?? null) : null;
        return (
          <div key={group.label} className="grid gap-3">
            <div className="flex items-center gap-1">
              <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">
                {group.label}
              </Heading>
              {group.reading && <ReadingHelp label={group.label} reading={group.reading} />}
            </div>
            {group.reading && <ReadingNote reading={group.reading} facts={group.facts} />}
            {group.facts.length > 0 ? (
              <ul aria-label={`${group.label}: answers`} className="grid gap-3">
                {group.facts.map((fact) => (
                  <FactItem
                    key={`${fact.sourceSystem} ${fact.factKey}`}
                    fact={fact}
                    member={member}
                    // A group no registry claims names each answer's source itself.
                    who={group.registry === null ? fact.sourceSystem : group.label}
                    reading={group.reading}
                    aboutNumber={group.registry !== null}
                    retry={retry}
                    refreshable={refreshable}
                    writes={writes}
                    SubHeading={multi ? 'h5' : 'h4'}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm">{absent(`No answer from ${group.label} is held.`)}</p>
            )}
          </div>
        );
      })}
      {secAdv && (
        <SecAdvFirms firms={secAdv.firms} looked={secAdv.looked} Heading={Heading} />
      )}
    </div>
  );
}

/**
 * What one registry last said under one key — its status in words, survey-api's reading of it
 * where there is one, its own date and our fetch time, the number's "Ask … again", and the
 * answer as stored — through the one rendering of a registry's answer (`RegistryFact`), which
 * the firm card's prefill preview shares.
 *
 * An `error` says the registry could not be asked, which is a statement about us and never
 * about the number — and that nothing asks again by itself: survey-api never retries an
 * `error` (its plan's first follow-up), so asking again is the retry, and where no refresh on
 * this page reaches the registry the copy says that instead of promising one.
 */
function FactItem({
  fact,
  member,
  who,
  reading: grouped,
  aboutNumber,
  retry,
  refreshable,
  writes,
  SubHeading
}: {
  fact: IAdminParticipantFact;
  member: IAdminParticipantDetail;
  /** The registry's name, or the answer's source where no group names one. */
  who: string;
  /** The reading group it is under (`READING_OF_FACT_KEY`), which says it in its own words. */
  reading: Nullable<FactReading>;
  /** Whether it is a registry's answer about a number the participant holds — false under
   *  "Other registry answers", whose answers name no number. */
  aboutNumber: boolean;
  /** The refresh that reaches this registry from this page, by the name its button says. */
  retry: Nullable<string>;
  /** The member's numbers this registry is asked about again by. */
  refreshable: IAdminParticipantIdentifier[];
  writes: INumberWrites;
  SubHeading: 'h4' | 'h5';
}) {
  const reading = readingOf(fact, member, SubHeading);
  return (
    <RegistryFact
      fact={fact}
      words={
        grouped
          ? readingWords(grouped, fact)
          : factWords(fact, who, retry, reading !== null, aboutNumber)
      }
      reading={reading}
      tone={grouped ? readingTone(fact) : undefined}
    >
      <Fact label="Fetched" value={formatDateTime(fact.fetchedAt)} />
      <Fact
        label="As of"
        value={instantOrAbsent(fact.asOf, 'The registry gave no date of its own.')}
      />
      {refreshable.length > 0 && (
        <div className="grid gap-2 sm:col-span-2">
          {refreshable.map((identifier) => (
            <NumberRefresh
              key={`${identifier.idType} ${identifier.value}`}
              participantId={member.participantId}
              identifier={identifier}
              pending={member.pendingLookups}
              writes={writes}
            />
          ))}
        </div>
      )}
    </RegistryFact>
  );
}

/** survey-api's reading of an answer, drawn as this app knows its shape — or null where it
 *  made none and the answer is its sentence and its payload alone. */
function readingOf(
  fact: IAdminParticipantFact,
  member: IAdminParticipantDetail,
  SubHeading: 'h4' | 'h5'
): React.ReactNode {
  const typed = brokerCheckSummary(fact);
  if (typed) return <BrokerCheckReading reading={typed} member={member} Heading={SubHeading} />;
  if (fact.summary !== null) {
    // A summary under a key this app has no type for is still survey-api's reading: whole.
    return <PayloadTree value={fact.summary} label="The answer as the system read it" />;
  }
  if (fact.factKey === PRIOR_CYCLE_SUMMARY && fact.status === 'ok') {
    return <PriorCycleTable payload={fact.payload} />;
  }
  return null;
}

/** The migration's one fact — last cycle's figures, stored as `{t, v}` values by name. */
const PRIOR_CYCLE_SUMMARY = 'prior_cycle.summary';

/**
 * `prior_cycle.summary` as a table of its members (R9) — each by the name it was stored under,
 * each value as stored, a `{t, v}` value by its `v`. The names are the migration's, and are
 * drawn as it wrote them rather than labelled: a label table here would be a copy of the ETL
 * that falls out of step with it. A payload that is not an object of members draws no table,
 * and is read under "As stored".
 */
function PriorCycleTable({ payload }: { payload: unknown }) {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;
  const members = Object.entries(payload as Record<string, unknown>);
  if (members.length === 0) return absent('Nothing stored.');
  return (
    <TableFrame>
      <Table aria-label="Last cycle’s figures">
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Value</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map(([name, value]) => (
            <TableRow key={name}>
              <TableCell className="font-mono text-xs">{name}</TableCell>
              <TableCell>
                <StoredValue value={storedValue(value)} name={name} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

/** A `{t, v}` value's `v`; anything else as it is. */
function storedValue(value: unknown): unknown {
  if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'v' in value) {
    return (value as { v: unknown }).v;
  }
  return value;
}

function StoredValue({ value, name }: { value: unknown; name: string }) {
  if (value === null || value === undefined) return absent('Not answered');
  if (typeof value === 'object') return <PayloadTree value={value} label={`${name} as stored`} />;
  return <>{String(value)}</>;
}

function factWords(
  fact: IAdminParticipantFact,
  who: string,
  retry: Nullable<string>,
  read: boolean,
  aboutNumber: boolean
): React.ReactNode {
  switch (fact.status) {
    case 'ok':
      // What is below and where the stored answer is: an explanation, one press away (SR-474).
      return read ? (
        <span className="inline-flex items-center gap-1">
          {who} answered with a record.
          <HelpPopover label={`About ${who}’s answer`}>
            <p>What the system reads from it is below, and the answer as stored under that.</p>
          </HelpPopover>
        </span>
      ) : (
        `${who} answered with a record — below, exactly as it was stored.`
      );
    case 'not_found':
      return aboutNumber
        ? `${who} answered, and holds no record for the number it was asked about.`
        : `${who} answered, and holds no record.`;
    case 'error':
      return (
        `${who} could not be asked when this was fetched — which says nothing about the ` +
        `${aboutNumber ? 'number' : 'participant'}. Nothing asks again by itself: ` +
        (retry
          ? `“Ask ${retry} again” on the number is the retry.`
          : `no refresh on this page reaches ${who}.`)
      );
    default:
      // Closed in the type, open on the wire.
      return `${who} answered “${fact.status as string}”, which this app has no words for.`;
  }
}

/**
 * Whether the SEC ADV register group is drawn, and over what: when survey-api names a firm, or
 * when the member holds a number it looks firms up by (`SEC_ADV_MATCHED_ID_TYPES`) — then saying
 * that no register firm has those numbers, which is an answer; for anybody else the register was
 * never asked, and the page says nothing of it.
 *
 * `firms` is typed as always sent, and it is by every survey-api carrying SR-214 — but this app
 * and the API are deployed apart, and a card read from an API before it has no such member. Then
 * nobody asked the register, and nothing is drawn: `?? []` would say that no firm matched.
 */
function secAdvShown(
  firms: IAdminParticipantSecAdvFirm[] | undefined,
  identifiers: IAdminParticipantIdentifier[]
): Nullable<{ firms: IAdminParticipantSecAdvFirm[]; looked: IAdminParticipantIdentifier[] }> {
  const looked = identifiers.filter((identifier) =>
    SEC_ADV_MATCHED_ID_TYPES.has(identifier.idType)
  );
  if (firms === undefined || (firms.length === 0 && looked.length === 0)) return null;
  return { firms, looked };
}

/**
 * The SEC ADV register's firms that the participant's numbers name (survey-api's SR-214,
 * `secAdvFirms`), each leading to its firm card, with what matched it — the other direction of the
 * firm card's participants.
 *
 * **A group of its own, after the registries'**, rather than inside one: a firm is matched by a
 * `SEC_NUMBER`, which sits under SEC Form ADV, or by an `ORG_CRD`, which sits under BrokerCheck,
 * and a firm matched by both would otherwise be split across two groups.
 *
 * **"No firm" is said of a register that may itself be empty or stale**, so the sentence leads to
 * the register's state rather than standing as a verdict on the numbers.
 *
 * **A match is not a verification**, and is not read as one: that a number names a register firm
 * says the number exists at the SEC, not that this participant is that firm. "Verify number" on
 * the number stays a person's decision. The firm's type is labelled through the catalogue, the
 * feed's spelling beside it, as on the firm card (the owner's ruling, 2026-09-25) — read only
 * when there is a firm to label.
 */
function SecAdvFirms({
  firms,
  looked,
  Heading
}: {
  firms: IAdminParticipantSecAdvFirm[];
  looked: IAdminParticipantIdentifier[];
  Heading: 'h3' | 'h4';
}) {
  return (
    <div className="grid gap-3">
      <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">SEC ADV register</Heading>
      {firms.length === 0 ? (
        <div className="grid gap-1 text-sm">
          <p>{absent('No firm in the register has one of these numbers:')}</p>
          <ul aria-label="Numbers no register firm has" className="grid gap-0.5">
            {looked.map((identifier) => (
              <li key={`${identifier.idType} ${identifier.value}`} className="flex flex-wrap gap-x-3">
                <span className="text-muted-foreground">{idTypeLabel(identifier.idType)}</span>
                <span className="font-mono break-all">{identifier.value}</span>
              </li>
            ))}
          </ul>
          <p className="text-muted-foreground">
            The register may be empty or out of date — whether its imports have run is under{' '}
            <RegisterStatusLink />.
          </p>
        </div>
      ) : (
        <SecAdvFirmList firms={firms} />
      )}
    </div>
  );
}

/** The firms survey-api named, each with its type labelled — the one part of the page that reads
 *  the catalogue, and only once there is a firm for it to label. */
function SecAdvFirmList({ firms }: { firms: IAdminParticipantSecAdvFirm[] }) {
  const labels = useSecAdvCatalogue();
  const typeValues = codeValues(labels.data, FIRM_TYPE);
  return (
    <>
      <p className="text-sm text-muted-foreground">
        The register firms these numbers name. A match is not a verification — that is “Verify
        number”, on the number.
      </p>
      <ul aria-label="SEC ADV register: firms" className="grid gap-3">
        {firms.map((firm) => (
          <li
            key={firm.firmCrd}
            className="grid gap-3 rounded-md border border-border p-4 text-sm sm:grid-cols-2"
          >
            <div className="sm:col-span-2">
              <FirmLink firm={firm} />
            </div>
            <Fact label="Type" value={<LabelledCode code={firm.firmType} values={typeValues} />} />
            <Fact label="Matched by" value={matchedByText(firm.matchedBy)} />
            <Fact label="SEC number" value={<span className="font-mono">{firm.secNumber}</span>} />
            <Fact label="Firm CRD" value={<span className="font-mono">{firm.firmCrd}</span>} />
          </li>
        ))}
      </ul>
    </>
  );
}
